import { useEffect, useMemo, useState } from "react";
import { CalmButton } from "@/components/CalmButton";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Calendar, MessageCircle, Trophy, Settings, Mail, HeartPulse, BarChart3, Wind, Sparkles, BookOpen, Shield, Mic, LogOut, ShieldAlert, Users, Inbox, Info, Quote, BookHeart, Sprout, MailOpen, Archive, Lightbulb, Gem, Camera, Gauge, HeartHandshake, Moon, PersonStanding, LifeBuoy, Stethoscope } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { MoreMenu } from "@/components/MoreMenu";
import { useAuth } from "@/context/AuthContext";
import OfflineBanner from "@/components/OfflineBanner";
import { api } from "@/lib/api";
import { INBOX_EVENT } from "@/pages/Inbox";
import { logError } from "@/lib/log";
import { PLAYBOOK_EVENT } from "@/pages/Playbook";

const MAIN = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/inbox", label: "Inbox", icon: Inbox },
  { to: "/calendar", label: "Calendar", icon: Calendar },
  { to: "/ai", label: "AI Chat", icon: MessageCircle },
  { to: "/achievements", label: "Badges", icon: Trophy },
  { to: "/settings", label: "Settings", icon: Settings },
];
const MORE = [
  { to: "/playbook", label: "What Helps Me", icon: Sprout },
  { to: "/checkin", label: "Check-In", icon: HeartPulse },
  { to: "/insights", label: "Insights", icon: BarChart3 },
  { to: "/breathing", label: "Breathing", icon: Wind },
  { to: "/meditation", label: "Meditation", icon: Sparkles },
  { to: "/journal", label: "Journal", icon: BookOpen },
  { to: "/voice", label: "Voice", icon: Mic },
  { to: "/supporters", label: "Supporters", icon: Users },
  { to: "/safety", label: "Safety", icon: Shield },
  { to: "/saved-quotes", label: "Saved Quotes", icon: Quote },
  { to: "/guides", label: "Guides you've read", icon: BookHeart },
  { to: "/garden", label: "My Garden", icon: Sprout },
  { to: "/hugs", label: "My Hugs", icon: HeartHandshake },
  { to: "/letters", label: "Letters to Future Me", icon: MailOpen },
  { to: "/sos", label: "SOS Calm", icon: Wind },
  { to: "/safety-plan-now", label: "Use my safety plan", icon: LifeBuoy },
  { to: "/bedtime", label: "Bedtime wind-down", icon: Moon },
  { to: "/body-map", label: "Body Map", icon: PersonStanding },
  { to: "/therapist-share", label: "Share with my therapist", icon: Stethoscope },
  { to: "/worry-box", label: "Worry Box", icon: Archive },
  { to: "/reframe", label: "Reframe a Thought", icon: Lightbulb },
  { to: "/gratitude", label: "Gratitude Jar", icon: Gem },
  { to: "/wellbeing", label: "Wellbeing Check", icon: Gauge },
  { to: "/joy-snaps", label: "Joy Snaps", icon: Camera },
  { to: "/contact", label: "Contact", icon: Mail },
];
const ABOUT = { to: "/about", label: "About", icon: Info };
const SETTINGS = MAIN.find((i) => i.to === "/settings");
const BOTTOM = MAIN.filter((i) => i !== SETTINGS);
const ADMIN = { to: "/admin", label: "Admin", icon: ShieldAlert };

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Badge = ({ n, testid, className = "" }) => (n > 0 ? (
  <span className={`text-[10px] font-bold text-white bg-mm-error rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center ${className}`} data-testid={testid}>{n > 99 ? "99+" : n}</span>
) : null);

const Item = ({ to, label, icon: I, end, badge }) => (
  <NavLink to={to} end={end} data-testid={`nav-${slug(label)}`} className={({ isActive }) => `mm-nav-link ${isActive ? "mm-nav-active" : ""}`}>
    <I size={19} /> <span className="flex-1">{label}</span><Badge n={badge} testid={`nav-${slug(label)}-badge`} />
  </NavLink>
);

const useInboxUnread = () => {
  const [n, setN] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    const load = () => api.get("/inbox/unread-count").then((r) => setN(r.data.unread)).catch((e) => logError("Unread count failed:", e));
    load();
    window.addEventListener(INBOX_EVENT, load);
    return () => window.removeEventListener(INBOX_EVENT, load);
  }, [pathname]);
  return n;
};

let pbCheckedAt = 0;
const usePlaybookNew = () => {
  const [n, setN] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    const load = () => { pbCheckedAt = Date.now(); api.get("/playbook/new-count").then((r) => setN(r.data.count)).catch((e) => logError("Playbook count failed:", e)); };
    if (Date.now() - pbCheckedAt > 60000) load();
    window.addEventListener(PLAYBOOK_EVENT, load);
    return () => window.removeEventListener(PLAYBOOK_EVENT, load);
  }, [pathname]);
  return n;
};

export default function Shell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const unread = useInboxUnread();
  const pbNew = usePlaybookNew();
  const moreItems = useMemo(() => [...MORE, SETTINGS, ABOUT, ...(user?.is_admin ? [ADMIN] : [])], [user?.is_admin]);
  return (
    <div className="min-h-screen mm-grain">
      <aside className="hidden lg:flex fixed top-[var(--mm-banner-h,0px)] bottom-0 left-0 w-64 flex-col border-r bg-mm-surface z-20 px-4 py-6" data-testid="sidebar">
        <NavLink to="/" className="flex items-center gap-3 px-2 mb-8">
          <div className="w-10 h-10 rounded-2xl mm-gradient flex items-center justify-center text-xl">🧠</div>
          <div className="font-display font-bold text-xl">Mindful Me</div>
        </NavLink>
        <nav className="flex flex-col gap-1 overflow-y-auto">
          {MAIN.map((i) => <Item key={i.to} {...i} badge={i.to === "/inbox" ? unread : 0} />)}
          <Item {...ABOUT} />
          <div className="text-xs uppercase tracking-wider text-mm-muted font-semibold mt-6 mb-2 px-3">Wellness</div>
          {MORE.map((i) => <Item key={i.to} {...i} badge={i.to === "/playbook" ? pbNew : 0} />)}
          {user?.is_admin && <Item {...ADMIN} />}
        </nav>
        <div className="mt-auto pt-4 border-t">
          <div className="flex items-center gap-3 px-3 mb-2">
            <Avatar url={user?.photo_url} name={user?.name} size={36} testId="sidebar-avatar" />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-mm-muted truncate">{user?.email}</div>
            </div>
          </div>
          <button data-testid="logout-button" onClick={async () => { await logout(); nav("/landing"); }} className="mm-nav-link w-full">
            <LogOut size={18} /> Log out
          </button>
        </div>
      </aside>

      <main className="relative z-10 lg:pl-64 pb-28 lg:pb-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-6 sm:pt-10">
          <OfflineBanner />
          <Outlet />
          <CalmButton />
        </div>
      </main>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-mm-surface border-t flex justify-around py-2" data-testid="bottom-nav">
        {BOTTOM.map(({ to, label, icon: I, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`bottom-nav-${slug(label)}`}
            className={({ isActive }) => `relative flex flex-col items-center gap-0.5 text-[10px] font-semibold px-1 py-1 rounded-xl transition-colors ${isActive ? "text-mm-primary" : "text-mm-muted"}`}>
            <I size={21} />{label}
            {to === "/inbox" && <Badge n={unread} testid="bottom-nav-inbox-badge" className="absolute -top-1 right-0" />}
          </NavLink>
        ))}
        <MoreMenu items={moreItems} />
      </nav>
    </div>
  );
}
